import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, X } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const empty = { company_name: "", contact: "", street: "", zip: "", city: "", email: "", vat_id: "" };

export default function Customers() {
  const { readOnly } = useAuth();
  const qc = useQueryClient();
  const { data: customers = [], isLoading } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const save = useMutation({
    mutationFn: (payload) => (editing ? api.put(`/customers/${editing}`, payload) : api.post("/customers", payload)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      toast.success(editing ? "Kunde aktualisiert" : "Kunde angelegt");
      close();
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/customers/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      toast.success("Kunde gelöscht");
    },
  });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c.id); setForm({ company_name: c.company_name, contact: c.contact, street: c.street, zip: c.zip, city: c.city, email: c.email, vat_id: c.vat_id }); setOpen(true); };
  const close = () => setOpen(false);

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Kunden</h1>
          <p className="text-sm text-slate-500 mt-1">Ihre Kontaktdatenbank für schnelle Rechnungen.</p>
        </div>
        <button onClick={openNew} disabled={readOnly} data-testid="add-customer-button" className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2.5 rounded-lg inline-flex items-center gap-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          <Plus className="h-4 w-4" /> Kunde hinzufügen
        </button>
      </div>

      {isLoading ? (
        <div className="text-sm text-slate-400 py-14 text-center">Lädt…</div>
      ) : customers.length === 0 ? (
        <div className="bg-white border border-slate-200/80 rounded-xl py-16 text-center">
          <Users className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">Noch keine Kunden. Legen Sie den ersten an.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {customers.map((c) => (
            <div key={c.id} className="bg-white border border-slate-200/80 rounded-xl p-5 group" data-testid={`customer-card-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display font-semibold truncate">{c.company_name || c.contact || "—"}</div>
                  {c.contact && c.company_name && <div className="text-sm text-slate-500 truncate">{c.contact}</div>}
                </div>
                <div className={`flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ${readOnly ? "hidden" : ""}`}>
                  <button onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`} className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => { if (window.confirm("Kunde löschen?")) del.mutate(c.id); }} data-testid={`delete-customer-${c.id}`} className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-3 text-sm text-slate-500 leading-relaxed">
                {c.street && <div>{c.street}</div>}
                {(c.zip || c.city) && <div>{c.zip} {c.city}</div>}
                {c.email && <div className="text-slate-400 truncate">{c.email}</div>}
                {c.vat_id && <div className="text-xs text-slate-400 mt-1">USt-IdNr.: {c.vat_id}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="customer-dialog">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-lg font-semibold">{editing ? "Kunde bearbeiten" : "Neuer Kunde"}</h2>
              <button onClick={close} className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <F label="Firma" testid="form-company" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} />
              <F label="Ansprechpartner" testid="form-contact" value={form.contact} onChange={(v) => setForm({ ...form, contact: v })} />
              <F label="Straße & Nr." testid="form-street" value={form.street} onChange={(v) => setForm({ ...form, street: v })} />
              <div className="grid grid-cols-3 gap-3">
                <F label="PLZ" testid="form-zip" value={form.zip} onChange={(v) => setForm({ ...form, zip: v })} />
                <div className="col-span-2"><F label="Ort" testid="form-city" value={form.city} onChange={(v) => setForm({ ...form, city: v })} /></div>
              </div>
              <F label="E-Mail" testid="form-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <F label="USt-IdNr." testid="form-vat" value={form.vat_id} onChange={(v) => setForm({ ...form, vat_id: v })} />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={close} className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Abbrechen</button>
              <button onClick={() => save.mutate(form)} disabled={save.isPending} data-testid="save-customer-button" className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium disabled:opacity-60">
                {save.isPending ? "Speichern…" : "Speichern"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function F({ label, testid, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-white border border-slate-200 rounded-lg px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
    </label>
  );
}
